import { Fragment, useContext } from 'react';
import { Filter } from 'components/filter/filters-next/Filter';
import { FilterGroupContext } from 'components/filter/filters-next/FilterGroupContext';
import { FilterProviderContext } from 'components/filter/filters-next/FilterProvider';
import { InnerGroupOperator } from 'components/filter/filters-next/InnerGroupOperator';
import { LabelStrategy } from 'components/filter/filters-next/LabelStrategy';
import { useFiltersByKey } from 'components/filter/filters-next/useFiltersByKey';
import { FilterValue, ServerKey } from 'components/filter/types';
import { cloneDeep } from 'lodash';

interface QuickFiltersBarProps {
    quickFilters?: ServerKey[];
}

const QuickFiltersBar = (props: QuickFiltersBarProps) => {
    const { quickFilters: quickFilterKeys = [] } = props;
    const { resource, cacheKey, readOnly } = useContext(FilterProviderContext);
    const { showInnerOperator, groupId } = useContext(FilterGroupContext);
    const { currentGroup } = useFiltersByKey(cacheKey, groupId);
    const filters = currentGroup?.filters ?? [];
    const [quickFilters] = partitionQuickFilters(filters, quickFilterKeys);

    return (
        <>
            {filters.map((filter, i) => (
                <div key={filter.id} className={'flex items-center gap-1.5'}>
                    <Filter
                        resource={resource}
                        cacheKey={cacheKey}
                        nodeId={filter.id}
                        strategyType={filter.serverKey}
                        isLegacy={filter.isLegacy}
                        readOnly={readOnly}
                        relationalServerKey={filter.relationalServerKey}
                    />
                    {showInnerOperator && filters.length - 1 !== i && <InnerGroupOperator />}
                </div>
            ))}
            <Fragment key={filters.length}>
                {quickFilterKeys
                    .filter(k => !quickFilters.find(f => f.serverKey === k)?.id)
                    .map(k => (
                        <Filter
                            key={k}
                            strategyType={k}
                            resource={resource}
                            readOnly={readOnly}
                            cacheKey={cacheKey}
                        >
                            <LabelStrategy strategy={k} />
                        </Filter>
                    ))}
            </Fragment>
        </>
    );
};

const partitionQuickFilters = (filters: FilterValue[], keys: ServerKey[]) => {
    const quickFilters: FilterValue[] = [];
    const mutableFilters = cloneDeep(filters);

    keys.forEach(k => {
        const filter = filters.find(f => f.serverKey === k);

        if (filter) {
            quickFilters.push(filter);
            const filterIndex = mutableFilters.findIndex(f => f.id === filter.id);
            mutableFilters.splice(filterIndex, 1);
        }
    });

    return [quickFilters, mutableFilters];
};

export { QuickFiltersBar };
export type { QuickFiltersBarProps };
